import QrCode from '../index.vue'
import sessionMixin from '@/shared/mixins/session.mixin'
import CONSTANTS from '@/shared/constants/constants'
import Swal from 'sweetalert2'

export default {
  name: 'QrCodeModal',
  mixins: [sessionMixin],
  props: {
    externalId: {type: Number, required: true},
    sourceId: {type: Number, required: true},
    onClose: { type: Function, required: true },
    onSave: { type: Function, required: true },
    endpoint: { type: String, required: true },
    showAttachType: { type: Boolean, default: true },
    requiredAttachType: { type: Boolean, default: true },
    useGallery: { type: Boolean, default: false },
    pollingInterval: { type: Number, default: 7000 },
    fileTypeOptions: {
      type: Array,
      default: () => ([
        { value: 'document', labelKey: 'qrcode.file_types.document' },
        { value: 'selfie', labelKey: 'qrcode.file_types.selfie' },
        { value: 'other', labelKey: 'qrcode.file_types.other' },
      ])
    },
  },
  components: {
    QrCode
  },
  data() {
    return {
      session: null,
      status: null,
      photos: [],
      pollingTimer: null,
      knownUrls: new Set(),
      typeAttachOptions: [],
      isClosed: false,
      separatorIndex: -1,
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  beforeUnmount() {
    this.stopPolling()
  },
  mounted() {
    this.typeAttachOptions = this.getFileTypes()
  },
  methods: {
    handleSessionCreated(session) {
      this.session = session
      this.startPolling()
    },
    handleStatusChange(status) {
      this.status = status
      const closedStates = ['expired', 'closed', 'cancelled']
      if (closedStates.includes((status || '').toLowerCase())) {
        this.stopPolling()
      }
    },
    handleError(err) {
      this.$emit('error', err)
    },
    handleEnabledSave() {
      if (this.session && this.status && this.status.toLowerCase() !== 'active') {
        return false
      }
      return this.photos.length > 0
    },
    doClose() {
      this.stopPolling()
      this.onClose()
    },
    doSave() {
      if (!this.handleEnabledSave()) return

      if (this.requiredAttachType && this.photos.some(photo => !photo.attachType)) {
        Swal.fire({
          title: 'Atenção',
          text: 'Por favor, selecione o tipo de arquivo para cada imagem.',
          icon: 'warning',
        })
        return
      }

      Swal.fire({
        title: 'Confirmar',
        text: 'Tem certeza de que deseja salvar os arquivos?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim',
        cancelButtonText: 'Não',
        cancelButtonColor: '#d33',
      }).then((result) => {
        if (result.isConfirmed) {
          this.onSave({
            session: this.session,
            status: this.status,
            photos: this.photos,
          })
          this.doClose()
        }
      })
    },
    extractFileName(url) {
      try {
        const clean = url.split('?')[0]
        const parts = clean.split('/')
        return decodeURIComponent(parts[parts.length - 1]) || 'arquivo'
      } catch (e) {
        return 'arquivo'
      }
    },

    extractFileType(url) {
      try {
        const clean = url.split('?')[0]
        const parts = clean.split('/')
        return parts[parts.length - 1].split('.').pop() || 'other'
      } catch (e) {
        return 'other'
      }
    },

    startPolling() {
      if (this.pollingTimer || this.isClosed || !this.useGallery) return
      this.checkPayload()
      this.pollingTimer = setInterval(this.checkPayload, this.pollingInterval)
    },

    stopPolling() {
      this.isClosed = true

      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    },

    async checkPayload() {
      if (!this.session || this.isClosed)
        return

      if (!this.session) return
      try {
        const payload = await this.getPayload()

        if (!payload) return

        this.mergePayload(payload)
      } catch (err) {
        this.stopPolling()
        this.handleError(err)
      }

      this.handleEnabledSave()
    },

    /**
     * O backend guarda `payload` como uma string contendo JSON serializado,
     * que pode ser tanto um array (`["gs://...", ...]`) quanto um objeto
     * (`{ urls: [...] }` ou similar). Aqui normalizamos os dois casos para
     * sempre devolver um array de URLs.
    */
    parsePayload(raw) {
      if (!raw) return []

      const tryParse = (value) => {
        if (typeof value !== 'string') return value
        try {
          return JSON.parse(value)
        } catch {
          return value
        }
      }

      let parsed = tryParse(raw)

      // Se ainda for string após o parse, tenta mais uma vez
      // (caso de serialização dupla: string -> string -> array).
      if (typeof parsed === 'string') {
        parsed = tryParse(parsed)
      }

      if (Array.isArray(parsed)) {
        return parsed
      }

      if (parsed && typeof parsed === 'object') {
        const candidateKeys = ['payload', 'Payload', 'urls', 'Urls']

        for (const key of candidateKeys) {
          if (key in parsed) {
            const value = tryParse(parsed[key])
            if (Array.isArray(value)) return value
          }
        }

        // Fallback: procura em qualquer campo, parseando strings também.
        for (const value of Object.values(parsed)) {
          const resolved = tryParse(value)
          if (Array.isArray(resolved)) return resolved
        }

        return []
      }

      return []
    },

    mergePayload(payload) {
      if (!payload) return

      const urls = this.parsePayload(payload)
      if (urls.length === 0) return

      urls.forEach(async (gsUrl) => {
        if (this.knownUrls.has(gsUrl)) return
        this.knownUrls.add(gsUrl)

        this.photos.push({
          id: gsUrl,
          previewUrl: await this.gsToHttpUrl(gsUrl),
          fileName: this.extractFileName(gsUrl),
          fileType: this.extractFileType(gsUrl),
          status: 'received',
        })
      })
    },

    async gsToHttpUrl(gsUrl) {
      const singerDownloadUrl = await this.getSignerUrlDownload([gsUrl])

      if (!singerDownloadUrl) return gsUrl

      return singerDownloadUrl
    },
    getFileTypes() {
      const types = [
        { value: 1, label: 'Exame anterior' },
        { value: 2, label: 'Anamnese' },
        { value: 3, label: 'Laudo anterior' },
        { value: 4, label: 'Pedido médico' },
        { value: 5, label: 'Exame criado' }
      ]
      // Pedido médico é o anexo mais usado: fica fixo no topo e os demais em ordem alfabética.
      const physicianOrder = types.filter(type => type.value === CONSTANTS.DICOM_PLUS_TIPO_ANEXO.PEDIDO_MEDICO)

      this.separatorIndex = 1

      const others = types
        .filter(type => type.value !== CONSTANTS.DICOM_PLUS_TIPO_ANEXO.PEDIDO_MEDICO)
        .sort((a, b) => a.label.localeCompare(b.label))

      return [...physicianOrder, ...others]
    },
  }
}
